import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useSupabaseUser } from "./use-supabase-user";
import { useUserRoles } from "./useUserRoles";

/**
 * Safe profile access hook that respects PII protection policies
 * 
 * This hook ensures that sensitive employee data (identity numbers, addresses,
 * emergency contacts, etc.) are only accessible to authorized users:
 * - Profile owner (can see their own full data)
 * - Super admins (can see all data)
 * - Team admins/managers (can see limited team member data)
 * - Other users (can only see public profile info)
 */

export interface SafeProfile {
  id: string;
  full_name: string | null;
  first_name: string | null;
  last_name: string | null;
  email: string | null;
  phone?: string | null; // Available in team view, not in public view
  mobile_phone?: string | null; // Only for owner/super admin
  role: string | null;
  status: string | null;
  avatar_url: string | null;
  address?: string | null; // Only for owner/super admin
  identity_number?: string | null; // Only for owner/super admin
  afm?: string | null; // Only for owner/super admin
  emergency_contact?: string | null; // Only for owner/super admin
  date_of_birth?: string | null; // Only for owner/super admin
  trietia?: number | null;
  license_expiry_date?: string | null;
  created_at?: string;
  updated_at?: string;
}

/**
 * Hook to safely fetch a single profile with appropriate access level
 */
export const useSafeProfile = (userId: string | undefined) => {
  const { user } = useSupabaseUser();
  const { isSuperAdmin } = useUserRoles();

  return useQuery({
    queryKey: ["safeProfile", userId],
    queryFn: async () => {
      if (!userId) return null;

      // Use the safe function that returns data based on access level
      const { data, error } = await supabase.rpc('get_safe_profile_data', {
        profile_user_id: userId
      });

      if (error) {
        console.error("Error fetching safe profile:", error);
        throw error;
      }

      return data?.[0] as SafeProfile | null;
    },
    enabled: !!userId && !!user,
    staleTime: 120_000, // 2 minutes
  });
};

/**
 * Hook to fetch multiple profiles with appropriate access level
 * Uses the restricted views for non-sensitive queries
 */
export const useSafeProfiles = (userIds: string[] | undefined) => {
  const { user } = useSupabaseUser();
  const { isSuperAdmin } = useUserRoles();

  return useQuery({
    queryKey: ["safeProfiles", userIds],
    queryFn: async () => {
      if (!userIds || userIds.length === 0) return [];

      // For super admins, use full profiles table
      if (isSuperAdmin) {
        const { data, error } = await supabase
          .from("profiles")
          .select("*")
          .in("id", userIds);

        if (error) throw error;
        return data as SafeProfile[];
      }

      // For others, use the team view which excludes highly sensitive data
      const { data, error } = await supabase
        .from("profiles_team_view")
        .select("*")
        .in("id", userIds);

      if (error) throw error;
      return data as SafeProfile[];
    },
    enabled: !!userIds && userIds.length > 0 && !!user,
    staleTime: 120_000,
  });
};

/**
 * Hook to fetch all visible profiles for the current user
 * Automatically uses the appropriate view based on user role
 */
export const useVisibleProfiles = () => {
  const { user } = useSupabaseUser();
  const { isSuperAdmin, isAdmin } = useUserRoles();

  return useQuery({
    queryKey: ["visibleProfiles", user?.id],
    queryFn: async () => {
      // Super admins can see full profiles
      if (isSuperAdmin) {
        const { data, error } = await supabase
          .from("profiles")
          .select("*");

        if (error) throw error;
        return data as SafeProfile[];
      }

      // Regular admins and managers see team view (no highly sensitive data)
      if (isAdmin) {
        const { data, error } = await supabase
          .from("profiles_team_view")
          .select("*");

        if (error) throw error;
        return data as SafeProfile[];
      }

      // Regular users only see public profile info
      const { data, error } = await supabase
        .from("profiles_public")
        .select("*");

      if (error) throw error;
      return data as SafeProfile[];
    },
    enabled: !!user,
    staleTime: 120_000,
  });
};

/**
 * Hook to check if current user can view full profile data for a specific user
 */
export const useCanViewFullProfile = (userId: string | undefined) => {
  const { user } = useSupabaseUser();
  const { isSuperAdmin } = useUserRoles();

  return useQuery({
    queryKey: ["canViewFullProfile", userId],
    queryFn: async () => {
      if (!userId) return false;
      
      // Own profile or super admin
      if (user?.id === userId || isSuperAdmin) {
        return true;
      }

      const { data, error } = await supabase.rpc('can_view_full_profile', {
        profile_user_id: userId
      });

      if (error) {
        console.error("Error checking profile access:", error);
        return false;
      }

      return data as boolean;
    },
    enabled: !!userId && !!user,
    staleTime: 60_000,
  });
};

/**
 * Utility to determine which profile fields should be shown in UI
 */
export const getSensitiveFields = () => {
  return [
    'identity_number',
    'afm',
    'emergency_contact',
    'date_of_birth',
    'address',
    'mobile_phone'
  ];
};

/**
 * Check if a field contains sensitive PII
 */
export const isSensitiveField = (fieldName: string): boolean => {
  return getSensitiveFields().includes(fieldName);
};
